import { Direction } from '@angular/cdk/bidi';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { FloatLabelType, MatFormFieldAppearance } from '@angular/material/form-field';
import { IconType } from '@ng-icons/core';
import { Subscription } from 'rxjs';
import {
  DECREASE,
  HOUR,
  INCREASE,
  MINUTE,
  SELECT_TIME,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { LanguageService } from 'src/app/@shared/services/language/language.service';

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

@Component({
    selector: 'mot-time-picker',
    templateUrl: './mot-time-picker.component.html',
    styleUrls: ['./mot-time-picker.component.scss'],
    standalone: false
})
export class MotTimePickerComponent implements ControlValueAccessor {
  @Output() onIconClick = new EventEmitter<MouseEvent>();
  @Output() onTimeChange = new EventEmitter<string>();

  @Input() displayErrors = true;
  @Input() floatLabelType: FloatLabelType = 'auto';
  @Input() label: string;
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() placeholder = 'ЧЧ:ММ';
  @Input() class: string;
  @Input() iconClass: string;
  @Input() icon: IconType;

  formControl: FormControl;
  hourControl = new FormControl<string>('00');
  minuteControl = new FormControl<string>('00');

  hours = HOURS_24;
  minutes = MINUTES;

  panelOpen = false;

  /** While input is focused, show this (user typing); otherwise displayValue */
  inputValue = '';
  isInputFocused = false;

  /** Unique id for error message (aria-describedby link). */
  errorId = 'mot-time-picker-err-' + Math.random().toString(36).slice(2, 11);

  direction: Direction = 'ltr';
  private languageSubscription: Subscription;

  /**
   * Translated aria-labels for the hour/minute stepper buttons.
   * Composed as "{Increase|Decrease} {hour|minute}" in the current language so SR users
   * understand the purpose of each stepper button (visual chevrons are aria-hidden).
   */
  increaseHourAriaLabel: string = `${INCREASE.hebrew} ${HOUR.hebrew}`;
  decreaseHourAriaLabel: string = `${DECREASE.hebrew} ${HOUR.hebrew}`;
  increaseMinuteAriaLabel: string = `${INCREASE.hebrew} ${MINUTE.hebrew}`;
  decreaseMinuteAriaLabel: string = `${DECREASE.hebrew} ${MINUTE.hebrew}`;
  selectTimeAriaLabel: string = SELECT_TIME.hebrew;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.panelOpen) return;
    if (this.elementRef.nativeElement.contains(event.target as Node)) return;
    this.closePanel();
  }

  ngOnInit(): void {
    if (this.ngControl) {
      this.formControl = this.ngControl.control as FormControl;
    }
    this.languageSubscription = this.languageService.selectedLanguage$.subscribe(
      (language) => {
        this.direction = language.direction;
        const inc = INCREASE[language.language];
        const dec = DECREASE[language.language];
        const hour = HOUR[language.language];
        const minute = MINUTE[language.language];
        this.increaseHourAriaLabel = `${inc} ${hour}`;
        this.decreaseHourAriaLabel = `${dec} ${hour}`;
        this.increaseMinuteAriaLabel = `${inc} ${minute}`;
        this.decreaseMinuteAriaLabel = `${dec} ${minute}`;
        this.selectTimeAriaLabel = SELECT_TIME[language.language];
      }
    );
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  get displayValue(): string {
    const h = this.hourControl.value ?? '00';
    const m = this.minuteControl.value ?? '00';
    return `${h}:${m}`;
  }

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: string | null): void {
    if (value && /^\d{1,2}:\d{1,2}$/.test(value)) {
      const [h, m] = value.split(':').map((v) => v.padStart(2, '0'));
      const hour = Math.min(23, Math.max(0, parseInt(h, 10)));
      const minute = Math.min(59, Math.max(0, parseInt(m, 10)));
      this.hourControl.setValue(hour.toString().padStart(2, '0'), { emitEvent: false });
      this.minuteControl.setValue(minute.toString().padStart(2, '0'), { emitEvent: false });
    } else {
      this.hourControl.setValue('00', { emitEvent: false });
      this.minuteControl.setValue('00', { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.hourControl.disable();
      this.minuteControl.disable();
    } else {
      this.hourControl.enable();
      this.minuteControl.enable();
    }
  }

  openPanel(): void {
    if (this.hourControl.disabled) return;
    this.panelOpen = true;
    this.onTouched();
    this.cdr.detectChanges();
  }

  closePanel(): void {
    this.commitToForm();
    this.panelOpen = false;
    this.cdr.detectChanges();
  }

  /** Update form value and emit (call only on blur or panel close) */
  private commitToForm(): void {
    this.onChange(this.displayValue);
    this.onTimeChange.emit(this.displayValue);
    this.formControl?.markAsTouched();
  }

  /** Update only local display state (no form commit) */
  syncFromPanel(): void {
    this.inputValue = this.displayValue;
    this.cdr.detectChanges();
  }

  setNow(): void {
    const now = new Date();
    this.hourControl.setValue(now.getHours().toString().padStart(2, '0'));
    this.minuteControl.setValue(now.getMinutes().toString().padStart(2, '0'));
    this.syncFromPanel();
  }

  stepHour(delta: number): void {
    const h = parseInt(this.hourControl.value ?? '0', 10);
    const next = (h + delta + 24) % 24;
    this.hourControl.setValue(next.toString().padStart(2, '0'));
    this.syncFromPanel();
  }

  stepMinute(delta: number): void {
    const m = parseInt(this.minuteControl.value ?? '0', 10);
    const next = (m + delta + 60) % 60;
    this.minuteControl.setValue(next.toString().padStart(2, '0'));
    this.syncFromPanel();
  }

  onInputFocus(): void {
    this.isInputFocused = true;
    this.inputValue = this.displayValue;
    this.cdr.detectChanges();
  }

  /** Open the panel on explicit activation (click), not on programmatic focus. */
  onInputClick(): void {
    this.openPanel();
  }

  onInputBlur(_event?: FocusEvent): void {
    this.isInputFocused = false;
    const parsed = this.parseTimeString(this.inputValue.trim());
    if (parsed) {
      this.hourControl.setValue(parsed.h);
      this.minuteControl.setValue(parsed.m);
    }
    this.inputValue = this.displayValue;
    this.cdr.detectChanges();
  }

  /**
   * When the panel opens on input focus but focus never enters the panel (spinners are tabindex="-1"),
   * Tab moves to the next control while the panel stays open. Close on actual focus leaving the host.
   * If relatedTarget is null (e.g. click on non-focusable panel), do not close — document:click still handles outside clicks.
   */
  @HostListener('focusout', ['$event'])
  onHostFocusOut(event: FocusEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      return;
    }
    const next = event.relatedTarget as Node | null;
    if (next && this.elementRef.nativeElement.contains(next)) {
      return;
    }
    if (next != null) {
      if (this.panelOpen) {
        this.closePanel();
      } else {
        this.commitToForm();
      }
    } else {
      this.commitToForm();
    }
    this.cdr.detectChanges();
  }

  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.panelOpen) {
      event.stopPropagation();
      this.closePanel();
    }
  }

  onTimeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const isDeleting = (event as InputEvent).inputType?.startsWith('delete');

    const digitsOnly = value.replace(/\D/g, '');
    if (!isDeleting && digitsOnly.length >= 2 && !value.includes(':')) {
      value = digitsOnly.slice(0, 2) + ':' + digitsOnly.slice(2);
    }

    if (value.includes(':')) {
      const [hPart, mPart] = value.split(':');
      let corrected = false;

      let h = hPart;
      if (hPart.length === 2 && parseInt(hPart, 10) > 23) {
        h = '23';
        corrected = true;
      }

      let m = mPart ?? '';
      if (mPart && mPart.length === 2 && parseInt(mPart, 10) > 59) {
        m = '59';
        corrected = true;
      }

      if (corrected) {
        value = h + ':' + m;
      }
    }

    if (value !== input.value) {
      input.value = value;
      input.setSelectionRange(value.length, value.length);
    }
    this.inputValue = value;

    const parsed = this.parseTimeString(this.inputValue.trim());
    if (parsed) {
      this.hourControl.setValue(parsed.h);
      this.minuteControl.setValue(parsed.m);
    }
    this.cdr.detectChanges();
  }

  private parseTimeString(raw: string): { h: string; m: string } | null {
    const match = raw.match(/^(\d{1,2}):(\d{1,2})$/);
    if (!match) return null;
    const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
    const m = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    return {
      h: h.toString().padStart(2, '0'),
      m: m.toString().padStart(2, '0'),
    };
  }

  getFirstError(): string {
    if (this.formControl?.errors) {
      const key = Object.keys(this.formControl.errors)[0];
      return this.formControl.errors[key];
    }
    return '';
  }

  handleIconClick(event: MouseEvent): void {
    event.stopPropagation();
    this.onIconClick.emit(event);
  }
}
